import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { I18N_KEYS } from "@/lib/i18nCatalog";
import { MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";

const PAGE = 60;

export default function FrenchStringsEditor() {
  const [data, setData] = useState({});
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState("");
  const [custom, setCustom] = useState({ key: "", value: "" });

  useEffect(() => {
    api.get("/admin/translations/fr/data")
      .then((r) => setData(r.data?.data || {}))
      .catch(() => setData({}));
  }, []);

  const keys = useMemo(() => {
    const junk = (k) => /^[('"]|Math\.|\.length|=>|\$\{/.test(k); // code fragments from catalog extraction
    const all = Array.from(new Set([...I18N_KEYS, ...Object.keys(data)])).filter((k) => !junk(k));
    const ql = q.trim().toLowerCase();
    return all.filter((k) => {
      if (onlyMissing && data[k]) return false;
      if (!ql) return true;
      return k.toLowerCase().includes(ql) || (data[k] || "").toLowerCase().includes(ql);
    }).sort();
  }, [data, q, onlyMissing]);

  const save = async (key, value) => {
    setErr(""); setSaved("");
    try {
      await api.put("/admin/translations/fr/entry", { key, value });
      setData((d) => {
        const n = { ...d };
        if (value) n[key] = value; else delete n[key];
        return n;
      });
      setSaved(key);
      setTimeout(() => setSaved(""), 2500);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="card mt-5" data-testid="french-strings-editor">
      <h2 className="text-xl font-bold font-display mb-1">French strings — edit in place</h2>
      <p className="text-sm text-[#000000] mb-3">
        Every button, label and message in the app. Changes go live for French-mode users as soon as you save a row.
      </p>
      {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#777]" />
          <input className="input-base !pl-9 w-full" placeholder="Search English or French…" value={q}
            data-testid="fr-strings-search" onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} />
        </div>
        <label className="flex items-center gap-1.5 text-sm font-semibold cursor-pointer">
          <input type="checkbox" checked={onlyMissing} data-testid="fr-strings-missing-only"
            onChange={(e) => { setOnlyMissing(e.target.checked); setLimit(PAGE); }} /> Untranslated only
        </label>
        <span className="text-xs text-[#555]">{keys.length} strings</span>
      </div>

      <div className="flex flex-col gap-1.5 max-h-[520px] overflow-y-auto pr-1">
        {keys.slice(0, limit).map((k) => (
          <Row key={k} k={k} value={data[k] || ""} onSave={save} justSaved={saved === k} />
        ))}
        {keys.length === 0 && (
          <div className="text-sm italic text-[#555] px-1 py-3">
            {onlyMissing ? "Everything is translated — nothing left to do here." : "No strings match your search."}
          </div>
        )}
        {keys.length > limit && (
          <button className="btn-ghost text-sm font-semibold border border-[#C4C8CE] rounded-full self-center px-4 my-2"
            onClick={() => setLimit((l) => l + PAGE)}>Show more ({keys.length - limit} left)</button>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
        <div className="text-sm font-semibold mb-1.5">Add a string that isn't listed</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input className="input-base text-sm" placeholder="Exact English text as it appears in the app"
            value={custom.key} data-testid="fr-strings-custom-key" onChange={(e) => setCustom({ ...custom, key: e.target.value })} />
          <div className="flex gap-2">
            <input className="input-base text-sm flex-1" placeholder="French translation"
              value={custom.value} data-testid="fr-strings-custom-value" onChange={(e) => setCustom({ ...custom, value: e.target.value })} />
            <button className="btn-primary text-sm" data-testid="fr-strings-custom-save" disabled={!custom.key.trim() || !custom.value.trim()}
              onClick={() => { save(custom.key.trim(), custom.value.trim()); setCustom({ key: "", value: "" }); }}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, value, onSave, justSaved }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const dirty = v !== value;
  return (
    <div className="grid sm:grid-cols-2 gap-2 items-center rounded-xl border border-[#E5E7EB] bg-white px-3 py-2">
      <div className="text-xs text-[#333] break-words">{k}</div>
      <div className="flex items-center gap-1.5">
        <input className={`input-base text-sm flex-1 ${!value && !v ? "border-[#F59E0B]" : ""}`} value={v}
          placeholder="(not translated yet)" onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && dirty) onSave(k, v.trim()); }} />
        {dirty ? (
          <button className="btn-primary text-xs !py-1.5" onClick={() => onSave(k, v.trim())}>Save</button>
        ) : justSaved ? (
          <CheckCircle size={18} weight="fill" color="#1a7f37" />
        ) : null}
      </div>
    </div>
  );
}
